import { normalizeConversationFilters } from './conversationTriggerFilterConfig';
import { normalizeInboxIdList } from './inboxIdsNormalize';
import { CONTENT_MATCH_OPERATOR_OPTIONS } from './messageTriggerFilterConfig';

/** Chaves por regra no array `condition_rules` do nó `kind: 'condition'`. */
export const CONDITION_ATTRIBUTES = [
  { value: 'status', label: 'Status da conversa' },
  { value: 'priority', label: 'Prioridade da conversa' },
  { value: 'inbox', label: 'Caixa de entrada' },
  { value: 'assignee', label: 'Agente atribuído' },
  { value: 'label', label: 'Etiqueta' },
  { value: 'message_content', label: 'Conteúdo da mensagem' },
];

const ATTRIBUTE_VALUES = new Set(CONDITION_ATTRIBUTES.map(a => a.value));

export const CONDITION_OPERATORS_BY_ATTRIBUTE = {
  status: [
    { value: 'equals', label: 'é' },
    { value: 'not_equals', label: 'não é' },
  ],
  priority: [
    { value: 'equals', label: 'é' },
    { value: 'not_equals', label: 'não é' },
    { value: 'is_empty', label: 'não está definida' },
  ],
  inbox: [
    { value: 'equals', label: 'é' },
    { value: 'not_equals', label: 'não é' },
    { value: 'in', label: 'é uma de' },
    { value: 'not_in', label: 'não é nenhuma de' },
  ],
  assignee: [
    { value: 'equals', label: 'é' },
    { value: 'not_equals', label: 'não é' },
    { value: 'is_unassigned', label: 'sem atribuição' },
  ],
  label: [
    { value: 'has', label: 'inclui' },
    { value: 'not_has', label: 'não inclui' },
  ],
  message_content: CONTENT_MATCH_OPERATOR_OPTIONS.filter(
    o => o.value !== 'none'
  ),
};

export const operatorsForAttribute = attr =>
  CONDITION_OPERATORS_BY_ATTRIBUTE[attr] ||
  CONDITION_OPERATORS_BY_ATTRIBUTE.status;

const newRuleId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `r-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const defaultRuleFields = () => ({
  condition_attribute: 'status',
  condition_operator: 'equals',
  condition_value: '',
  condition_inbox_ids: [],
});

/**
 * Uma regra isolada (sem `id` gera novo id).
 */
export function normalizeSingleRule(raw) {
  const src = raw && typeof raw === 'object' ? { ...raw } : {};
  const id =
    src.id !== null && src.id !== undefined && String(src.id).length
      ? String(src.id)
      : newRuleId();

  if (!ATTRIBUTE_VALUES.has(String(src.condition_attribute))) {
    const legacyCf = src.conversation_filters
      ? normalizeConversationFilters(src.conversation_filters)
      : null;
    if (legacyCf?.inbox_ids?.length) {
      src.condition_attribute = 'inbox';
      src.condition_operator = 'in';
      src.condition_inbox_ids = normalizeInboxIdList(legacyCf.inbox_ids);
      src.condition_value = '';
    }
  }

  const out = { ...defaultRuleFields(), ...src, id };
  let attr = ATTRIBUTE_VALUES.has(out.condition_attribute)
    ? out.condition_attribute
    : 'status';
  out.condition_attribute = attr;

  const allowedOps = operatorsForAttribute(attr).map(o => o.value);
  if (!allowedOps.includes(out.condition_operator)) {
    [out.condition_operator] = allowedOps;
  }

  out.condition_value =
    out.condition_value !== null &&
    out.condition_value !== undefined &&
    String(out.condition_value).length
      ? String(out.condition_value)
      : '';
  out.condition_inbox_ids = normalizeInboxIdList(out.condition_inbox_ids);

  if (out.condition_operator === 'is_unassigned') {
    out.condition_value = '';
  }
  if (attr === 'priority' && out.condition_operator === 'is_empty') {
    out.condition_value = '';
  }

  return {
    id,
    condition_attribute: out.condition_attribute,
    condition_operator: out.condition_operator,
    condition_value: out.condition_value,
    condition_inbox_ids: out.condition_inbox_ids,
  };
}

export function createEmptyConditionRule() {
  return normalizeSingleRule({});
}

/**
 * Normaliza `node.data` do nó condição: só `condition_rules`.
 * Migra formato antigo (campos planos ou só conversation_filters).
 */
export function normalizeWorkflowConditionData(raw) {
  const src = raw && typeof raw === 'object' ? { ...raw } : {};
  let rules = Array.isArray(src.condition_rules) ? src.condition_rules : null;

  if (!rules || rules.length === 0) {
    const flat = {
      condition_attribute: src.condition_attribute,
      condition_operator: src.condition_operator,
      condition_value: src.condition_value,
      condition_inbox_ids: src.condition_inbox_ids,
      conversation_filters: src.conversation_filters,
    };
    if (
      flat.condition_attribute ||
      flat.condition_operator ||
      flat.condition_value ||
      (flat.condition_inbox_ids && flat.condition_inbox_ids.length) ||
      flat.conversation_filters
    ) {
      rules = [normalizeSingleRule(flat)];
    } else {
      rules = [normalizeSingleRule({})];
    }
  } else {
    rules = rules.map(r => normalizeSingleRule(r));
  }

  return { condition_rules: rules };
}

export const conditionAttributeLabel = value =>
  CONDITION_ATTRIBUTES.find(a => a.value === value)?.label || value;

export const conditionOperatorLabel = (attribute, operator) =>
  operatorsForAttribute(attribute).find(o => o.value === operator)?.label ||
  operator;

/** Saída fixa quando nenhuma regra anterior se aplica. */
export const CONDITION_ELSE_HANDLE = 'else';
